import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { FlowNavigationProvider } from "@/components/flow/flow-navigation-context";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { apiErrorMessage } from "@/lib/api/_client";
import { useAddPublicGoal, useDeletePublicGoal } from "@/lib/hooks/use-public-flow/use-goals";
import { useT } from "@/lib/i18n/use-t";
import type { FlowPage } from "@/lib/types/flow";
import type { GoalListItem } from "@/lib/types/goal";
import { PublicGoalNav } from "./flow/public-goal-nav";
import { PublicPortfolioPageRenderer } from "./flow/public-portfolio-page-renderer";
import { T } from "./multi-goal-flow-body.i18n";
import {
  computePageBuckets,
  makeIsGoalPageVisible,
  makeIsPortfolioPageVisible,
} from "./public-flow-utils";
import { PublicPageRenderer, type PublicPageRendererContext } from "./public-page-renderer";

type MultiGoalFlowBodyProps = {
  allPages: any[];
  ctx: PublicPageRendererContext;
};

/**
 * Multi-goal public flow body (QP-150 3-level nav). Uses the same GoalContextView
 * architecture as the advisor: L1 goal/global tabs → L2 goal pages + portfolio
 * instances → L3 portfolio page sub-nav.
 */
export function MultiGoalFlowBody({ allPages, ctx }: MultiGoalFlowBodyProps) {
  const t = useT(T);
  const goals = ctx.goals;
  const { isCompleted } = ctx;

  // Option D — client goal management.
  const addGoalMutation = useAddPublicGoal();
  const deleteGoalMutation = useDeletePublicGoal();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [goalToDelete, setGoalToDelete] = useState<string | null>(null);

  // Active goal state with ref-based guard (same pattern as advisor GoalListWidget).
  const [currentGoalId, setCurrentGoalId] = useState<string | null>(null);
  const currentGoalIdRef = useRef<string | null>(null);
  const hasInitializedRef = useRef(false);

  const setCurrentGoal = (id: string | null) => {
    setCurrentGoalId(id);
    currentGoalIdRef.current = id;
  };

  // Deletion safety net — only depends on goals, reads from ref.
  useEffect(() => {
    const id = currentGoalIdRef.current;
    if (id === null) return;
    if (goals.length === 0) return;
    if (!goals.some((g) => g.id === id)) {
      setCurrentGoal(goals[0].id);
    }
  }, [goals]);

  const pageBuckets = useMemo(
    () => computePageBuckets(allPages, ctx.formValues, goals, ctx.fieldTypes),
    [allPages, ctx.formValues, goals, ctx.fieldTypes],
  );

  // QL-120 — the buckets only group goal and portfolio pages; the page-level rule is resolved here,
  // per goal and, for portfolio pages, per (goal × portfolio) pair.
  const isGoalPageVisible = useMemo(
    () => makeIsGoalPageVisible(ctx.formValues, goals, ctx.fieldTypes),
    [ctx.formValues, goals, ctx.fieldTypes],
  );

  const isPortfolioPageVisible = useMemo(
    () => makeIsPortfolioPageVisible(ctx.formValues, goals, ctx.fieldTypes),
    [ctx.formValues, goals, ctx.fieldTypes],
  );

  // One-time init — default to first goal or first global-before page.
  useEffect(() => {
    if (hasInitializedRef.current || goals.length === 0) return;
    hasInitializedRef.current = true;
    if (pageBuckets.globalPagesBefore.length === 0) {
      setCurrentGoal(goals[0].id);
    }
  }, [goals, pageBuckets.globalPagesBefore.length]);

  // Render callbacks — same pattern as the advisor FlowInstanceWizard.
  const renderGoalPage = (page: FlowPage, goalId: string) => (
    <PublicPageRenderer page={page} goalId={goalId} ctx={ctx} />
  );

  const renderPortfolioPage = (page: FlowPage, goalId: string, portfolio: any) => (
    <PublicPortfolioPageRenderer
      page={page}
      portfolio={portfolio}
      goalId={goalId}
      goals={goals}
      isCompleted={isCompleted}
      flowLanguage={ctx.language}
      formValues={ctx.formValues}
      fieldTypes={ctx.fieldTypes}
      chartColors={ctx.chartColors}
      portfolioCosts={ctx.portfolioCosts}
      currency={ctx.flowCurrency}
      showValidationErrors={ctx.showValidationErrors}
      markers={ctx.markers}
      dynamicPortfoliosEnabled={ctx.dynamicPortfoliosEnabled}
      modelDeviationEnabled={ctx.modelDeviationEnabled}
    />
  );

  const renderGlobalPage = (page: FlowPage) => (
    <PublicPageRenderer page={page} goalId={goals[0]?.id ?? null} ctx={ctx} />
  );

  // Goal add/delete handlers.
  const handleAddGoal = () => {
    if (goals.length >= ctx.maxGoals) {
      toast.error(t.maxGoalsReached, {
        description: t.maxGoalsDescription(ctx.maxGoals),
      });
      return;
    }
    // No goalType: the service applies its default; the goal page's picker
    // sets the real one (a configured collection rejects a hard-coded value).
    // No name either: the service names it in the flow language (QL-710).
    addGoalMutation.mutate(
      { lang: ctx.language },
      {
        onSuccess: (newGoal: { id: string }) => {
          setCurrentGoal(newGoal.id);
        },
        onError: (err) => {
          // QL-593 — the server's cap wins over the button; its
          // max_goals_reached message carries the current cap.
          toast.error(apiErrorMessage(err, t.failedToCreateGoal));
        },
      },
    );
  };

  const handleDeleteGoal = (goalId: string) => {
    if (goals.length === 1) {
      toast.error(t.cannotDeleteLastGoal, { description: t.cannotDeleteLastGoalDescription });
      return;
    }
    setGoalToDelete(goalId);
    setConfirmDeleteOpen(true);
  };

  const handleDeleteGoalConfirm = () => {
    if (!goalToDelete) return;
    const deletedId = goalToDelete;
    deleteGoalMutation.mutate(deletedId, {
      onSuccess: () => {
        toast.success(t.goalDeleted);
        if (currentGoalId === deletedId) {
          const remaining = goals.filter((g) => g.id !== deletedId);
          setCurrentGoal(remaining.length > 0 ? remaining[0].id : null);
        }
      },
      onError: (err) => toast.error(apiErrorMessage(err, t.failedToDeleteGoal)),
    });
    setGoalToDelete(null);
  };

  return (
    <FlowNavigationProvider onGoalChange={setCurrentGoal}>
      <PublicGoalNav
        goals={goals as unknown as GoalListItem[]}
        goalPages={pageBuckets.goalPages as unknown as FlowPage[]}
        portfolioPages={pageBuckets.portfolioPages as unknown as FlowPage[]}
        isGoalPageVisible={isGoalPageVisible}
        isPortfolioPageVisible={isPortfolioPageVisible}
        globalPagesBefore={pageBuckets.globalPagesBefore as unknown as FlowPage[]}
        globalPagesAfter={pageBuckets.globalPagesAfter as unknown as FlowPage[]}
        multiPortfolio={ctx.multiPortfolio}
        isCompleted={isCompleted}
        canAddGoal={goals.length < ctx.maxGoals && !addGoalMutation.isPending}
        onAddGoal={handleAddGoal}
        onDeleteGoal={handleDeleteGoal}
        currentGoalId={currentGoalId}
        onCurrentGoalChange={setCurrentGoal}
        renderGoalPage={renderGoalPage}
        renderPortfolioPage={renderPortfolioPage}
        renderGlobalPage={renderGlobalPage}
        showValidationErrors={ctx.showValidationErrors}
        markers={ctx.markers}
      />
      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title={t.deleteGoalTitle}
        description={t.deleteGoalDescription}
        confirmText={t.delete}
        cancelText={t.cancel}
        onConfirm={handleDeleteGoalConfirm}
        variant="destructive"
      />
    </FlowNavigationProvider>
  );
}
